import React, { useState } from 'react';
import { BaseModal } from './BaseModal';
import { Button, Badge } from './BaseComponents';
import { Question, FlagQuestionRequest } from '../types';
import { flagQuestion } from '../services/questionReviewService';
import { optionSeed, shuffledOptionOrder } from '../utils/optionOrder';

export interface QuestionFlagModalProps {
  isOpen: boolean;
  onClose: () => void;
  question: Question;
  onSuccess?: () => void;
  /** Attempt id the options were shuffled by, so they are listed as the student saw them (#150). */
  shuffleSeed?: string;
}

/**
 * QuestionFlagModal - Modal for flagging questions for manual review
 *
 * Allows users to flag questions they believe have incorrect answers
 * or need manual review by administrators/teachers.
 */
export const QuestionFlagModal: React.FC<QuestionFlagModalProps> = ({
  isOpen,
  onClose,
  question,
  onSuccess,
  shuffleSeed,
}) => {
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Common flag reasons for quick selection
  const commonReasons = [
    'Incorrect answer provided',
    'Question is unclear or ambiguous',
    'Multiple correct answers possible',
    'Calculation error in solution',
    'Outdated information',
    'Typo or formatting issue',
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!reason.trim()) {
      setError('Please provide a reason for flagging this question');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const request: FlagQuestionRequest = {
        reason: reason.trim(),
      };

      await flagQuestion(question.questionId, request);

      // Close modal and notify parent of success
      setReason('');
      onClose();

      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to flag question. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleQuickReasonSelect = (selectedReason: string) => {
    setReason(selectedReason);
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setReason('');
      setError(null);
      onClose();
    }
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={handleClose}
      className="max-w-2xl w-full"
      closeOnOverlayClick={!isSubmitting}
    >
      <div className="p-6 overflow-x-hidden">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
          <h2 className="text-xl font-semibold text-gray-900">
            Flag Question for Review
          </h2>
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className="bg-gray-100 text-gray-800 text-xs whitespace-nowrap">
              Question #{question.questionId}
            </Badge>
            <Badge className={`text-xs whitespace-nowrap ${
              question.difficulty === 'easy' ? 'bg-green-100 text-green-800' :
              question.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-800' :
              'bg-red-100 text-red-800'
            }`}>
              {question.difficulty}
            </Badge>
          </div>
        </div>

        {/* Question Preview */}
        <div className="mb-6 p-4 bg-gray-50 rounded-lg overflow-x-hidden">
          <h3 className="font-medium text-gray-900 mb-2">Question:</h3>
          <p className="text-gray-700 text-sm leading-relaxed break-words">
            {question.questionDescription}
          </p>

          {question.optionsJson && Array.isArray(question.optionsJson) && (
            <div className="mt-3">
              <h4 className="font-medium text-gray-700 text-sm mb-2">Options:</h4>
              <div className="space-y-1">
                {shuffledOptionOrder(question.optionsJson.length, optionSeed(shuffleSeed, question.questionId)).map((index, position) => (
                  <div key={index} className="text-sm text-gray-600 break-words">
                    <span className="font-medium">{String.fromCharCode(65 + position)}:</span> {(question.optionsJson as string[])[index]}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-3 pt-3 border-t border-gray-200">
            <div className="text-sm flex flex-wrap items-center gap-2">
              <span className="font-medium text-gray-700">Current Answer:</span>
              <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs break-words">
                {question.answerKey}
              </span>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          {/* Quick Reason Selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Common Issues (click to select):
            </label>
            <div className="flex flex-wrap gap-2">
              {commonReasons.map((commonReason) => (
                <button
                  key={commonReason}
                  type="button"
                  onClick={() => handleQuickReasonSelect(commonReason)}
                  className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                    reason === commonReason
                      ? 'bg-indigo-100 text-indigo-800 border-indigo-300'
                      : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
                  }`}
                  disabled={isSubmitting}
                >
                  {commonReason}
                </button>
              ))}
            </div>
          </div>

          {/* Custom Reason Input */}
          <div className="mb-6">
            <label htmlFor="flag-reason" className="block text-sm font-medium text-gray-700 mb-2">
              Reason for flagging this question:
            </label>
            <textarea
              id="flag-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Please describe why you think this question needs review..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 resize-none"
              rows={4}
              maxLength={1000}
              disabled={isSubmitting}
              required
            />
            <div className="flex justify-between items-center mt-1">
              <p className="text-xs text-gray-500">
                Be specific about the issue to help reviewers understand the problem.
              </p>
              <span className="text-xs text-gray-500">
                {reason.length}/1000
              </span>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* Buttons */}
          <div className="flex justify-end space-x-3">
            <Button
              type="button"
              variant="secondary"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isSubmitting || !reason.trim()}
            >
              {isSubmitting ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Flagging...
                </span>
              ) : (
                'Flag for Review'
              )}
            </Button>
          </div>
        </form>

        {/* Disclaimer */}
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
          <p className="text-xs text-blue-700">
            <strong>Note:</strong> Flagged questions will be reviewed by teachers/administrators.
            If the answer is corrected, scores for all affected quizzes will be automatically updated.
          </p>
        </div>
      </div>
    </BaseModal>
  );
};

export default QuestionFlagModal;